import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { BackofficeUser } from "../types/BackofficeUser";
import { useDrawer } from "../provider/DrawerProvider";
import EditActivity from "../components/Activities/EditActivity/EditActivity";
import CreateActivity from "../components/Activities/EditActivity/CreateActivity";
import ComposeEmailOrMessageForm from "../components/Case/CaseWriteEmail/ComposeEmailOrMessageForm";
import EditNote from "../components/Case/CaseCreateNote/EditNote";
import CommunicationMedium from "../components/Case/CaseBeaAndFax/CommunicationMedium";
import AddInquiryToCustomerDrawer from "../components/Case/CaseModals/AddInquiryToCustomerDrawer";
import { ProductUnion } from "../types/ProductUnion";
import CaseDescriptionEdit from "../components/Case/CaseDescriptionEdit/CaseDescriptionEdit";
import DocumentEditorPanel from "../components/DocumentEditor/DocumentEditorPanel";
import { getIriOfSubEntity } from "../services/utils";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../services/ReactQuery/reactQueryService";
import { AiChatMessage } from "../types/AIChat/AiChatMessage";
import { HydraCollectionResult } from "../types/HydraCollectionResult";
import { useOptionalAiChat } from "../provider/AiChatProvider";

type UseDrawerConfigProps = {
  product: ProductUnion | null;
  currentUser: BackofficeUser;
  refreshActivities: Function;
  defaultAssignedUser: string;
};

export const useCaseRelatedDrawerConfig = ({
  product,
  currentUser,
  refreshActivities,
  defaultAssignedUser,
}: UseDrawerConfigProps): { title: string; content: ReactNode; } | null => {
  const { drawerState, closeDrawer } = useDrawer();

  //auto-refresh step-edit form drawers when the AI patches the step payload: remounting with a
  //version-derived key re-initializes the form. The remount only ever happens while the drawer is
  //locked (AI edits are always triggered by an own prompt, see DrawerAiEditLock), so no input is lost
  const aiChat = useOptionalAiChat();
  const activeEditStepId = aiChat?.activeEditStepId ?? null;
  //latest server state of the edited step, selected from the same ai_chat_messages cache entry the chat
  //subscribes to (same queryKey, so no extra request); refreshes via the threadUpdated invalidation.
  //This selection must live here (drawer-scoped) and not in the accordion: the chat window can be
  //closed while the drawer stays open
  const activeThreadId = aiChat?.activeThreadId ?? null;
  const aiChatMessagesFilter = { aiChatThread: activeThreadId };
  const { data: aiChatMessagesResponse } = useQuery<HydraCollectionResult<AiChatMessage>>({
    queryKey: queryKeys.collection("ai_chat_messages", aiChatMessagesFilter),
    queryFn: () => fetchCollection("ai_chat_messages", aiChatMessagesFilter),
    enabled: !!activeThreadId && activeEditStepId !== null,
  });
  const freshExecutionStep = (aiChatMessagesResponse?.["hydra:member"] || [])
    .flatMap((message) => message.executionJob?.steps || [])
    .find((jobStep) => jobStep.id === activeEditStepId);
  const stepDrawerPayload = freshExecutionStep?.payload ?? null;
  //only AI edits bump the key: user autosaves refetch the payload too, but must not remount the open form mid-typing
  const [aiEditVersion, setAiEditVersion] = useState(0);
  const lastAiPayloadRef = useRef<unknown>(null);
  useEffect(() => {
    //seed with the payload present at open, so a pre-existing AI edit does not remount right after opening
    lastAiPayloadRef.current = freshExecutionStep?.payload ?? null;
  }, [activeEditStepId]);
  useEffect(() => {
    if (stepDrawerPayload?.lastEditedBy !== "ai") {
      return;
    }
    if (!_.isEqual(stepDrawerPayload, lastAiPayloadRef.current)) {
      lastAiPayloadRef.current = stepDrawerPayload;
      setAiEditVersion((version) => version + 1);
    }
  }, [stepDrawerPayload]);

  const stepDrawerKey = activeEditStepId !== null ? `${activeEditStepId}-${aiEditVersion}` : null;

  //EditActivity re-initializes its form whenever the identity of the `activity` prop changes, so the live
  //payload identity must stay frozen between AI edits (useMemo is load-bearing here, not an optimization):
  //user-autosave refetches would otherwise churn the open form; AI edits remount via the outer key anyway
  const stepActivityDraft = useMemo(() => stepDrawerPayload, [stepDrawerKey]);

  if (!product) {
    return null;
  }

  switch (drawerState?.type) {
    case "activity": {
      if (currentUser.isExternal) {
        return null;
      }

      const activity = stepActivityDraft ?? drawerState?.entityData;
      const isEdit = !!activity;

      return {
        title: isEdit ? "Aktivität bearbeiten" : "Aktivität erstellen",
        content: isEdit ? (
          <EditActivity
            key={stepDrawerKey}
            renderInDrawer={true}
            activity={activity}
            closeDialog={closeDrawer}
            updateActivities={refreshActivities}
            productClassName={product.productClassName}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
            open={true}
          />
        ) : (
          <CreateActivity
            product={product}
            closeDialog={closeDrawer}
            updateActivities={() => refreshActivities()}
            caseId={product.backofficeCase["@id"]}
            defaultAssignedUser={defaultAssignedUser}
          />
        ),
      };
    }

    case "message": {
      const message = stepDrawerPayload ?? drawerState?.entityData;
      const isResponse = message && getIriOfSubEntity(message, "conversation");
      return {
        title: isResponse ? "Nachricht beantworten" : "Nachricht schreiben",
        content: (
          <ComposeEmailOrMessageForm
            key={stepDrawerKey}
            product={product}
            closeDialog={closeDrawer}
            customerCommunicationOnly={drawerState?.typeSpecificData?.customerCommunicationOnly}
            message={message}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "note": {
      const note = stepDrawerPayload ?? drawerState?.entityData;
      const isEdit = !!note;

      return {
        title: isEdit ? "Notiz bearbeiten" : "Notiz erstellen",
        content: (
          <EditNote
            key={stepDrawerKey}
            product={product}
            closeDialog={closeDrawer}
            note={note}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "beaFax": {
      if (currentUser.isExternal) {
        return null;
      }

      const communicationDraft = stepDrawerPayload ?? drawerState?.entityData;

      return {
        title: "beA/Fax versenden",
        content: (
          <CommunicationMedium
            key={stepDrawerKey}
            product={product}
            handleClose={closeDrawer}
            communicationDraft={communicationDraft}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "inquiryToCustomer":
      if (currentUser.isExternal) {
        return null;
      }

      const inquiryDraft = stepDrawerPayload ?? drawerState?.entityData;
      const isInquiryEdit = !!inquiryDraft;

      return {
        title: isInquiryEdit ? "Nachricht mit Anfrage beantworten" : "Anfrage stellen",
        content: (
          <AddInquiryToCustomerDrawer
            key={stepDrawerKey}
            product={product}
            handleClose={closeDrawer}
            draft={inquiryDraft}
            conversationId={drawerState.typeSpecificData?.conversationId}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };

    case "email": {
      if (currentUser.isExternal) {
        return null;
      }

      return {
        title: "E-Mail bearbeiten",
        content: (
          <ComposeEmailOrMessageForm
            key={stepDrawerKey}
            product={product}
            closeDialog={closeDrawer}
            //the email drawer only exists for sendEmail execution steps whose payload must stay email-shaped
            emailCommunicationOnly={true}
            emailDraft={stepDrawerPayload}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    case "caseDescription":
      return {
        title: "Fallzusammenfassung",
        content: <CaseDescriptionEdit handleClose={closeDrawer} />,
      };

    case "documentEditor": {
      if (currentUser.isExternal) {
        return null;
      }
      //documentEditor drawers exist only for steps; thread switches close the drawer, so the live payload is always there
      if (!stepDrawerPayload) {
        return null;
      }

      return {
        title: stepDrawerPayload.title || "Dokument bearbeiten",
        content: (
          <DocumentEditorPanel
            key={stepDrawerKey}
            payload={stepDrawerPayload}
            overwrittenHandleSave={drawerState.overwrittenHandleSave}
          />
        ),
      };
    }

    default:
      return null;
  }
};
